"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Offering = void 0;
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const internal_1 = require("../../../internal");
const stos_1 = require("../../../middleware/queries/stos");
const conversion_1 = require("../../../utils/conversion");
const internal_2 = require("../../../utils/internal");
/**
 * Represents an Asset Offering in the Polymesh blockchain
 */
class Offering extends internal_1.Entity {
    /**
     * @hidden
     */
    constructor(identifiers, context) {
        super(identifiers, context);
        const { id, assetId } = identifiers;
        this.id = id;
        this.asset = new internal_1.FungibleAsset({ assetId }, context);
        this.freeze = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: () => [internal_1.toggleFreezeOffering, { asset: this.asset, id, freeze: true }],
            voidArgs: true,
        }, context);
        this.unfreeze = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: () => [internal_1.toggleFreezeOffering, { asset: this.asset, id, freeze: false }],
            voidArgs: true,
        }, context);
        this.close = (0, internal_2.createProcedureMethod)({ getProcedureAndArgs: () => [internal_1.closeOffering, { asset: this.asset, id }], voidArgs: true }, context);
        this.modifyTimes = (0, internal_2.createProcedureMethod)({ getProcedureAndArgs: args => [internal_1.modifyOfferingTimes, Object.assign({ asset: this.asset, id }, args)] }, context);
        this.invest = (0, internal_2.createProcedureMethod)({ getProcedureAndArgs: args => [internal_1.investInOffering, Object.assign({ asset: this.asset, id }, args)] }, context);
    }
    /**
     * @hidden
     * Check if a value is of type {@link UniqueIdentifiers}
     */
    static isUniqueIdentifiers(identifier) {
        const { id, assetId } = identifier;
        return id instanceof bignumber_js_1.default && typeof assetId === 'string';
    }
    // eslint-disable-next-line require-jsdoc
    details(callback) {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { sto }, }, }, id, asset, context, } = this;
            const assembleResult = (rawFundraiser, rawName) => {
                return (0, conversion_1.fundraiserToOfferingDetails)(rawFundraiser.unwrap(), rawName.unwrap(), context);
            };
            const rawAssetId = (0, conversion_1.assetToMeshAssetId)(asset, context);
            const rawU64 = (0, conversion_1.bigNumberToU64)(id, context);
            const fetchName = () => sto.fundraiserNames(rawAssetId, rawU64);
            if (callback) {
                context.assertSupportsSubscription();
                const fundraiserName = yield fetchName();
                return sto.fundraisers(rawAssetId, rawU64, fundraiserData => {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- callback errors should be handled by the caller
                    callback(assembleResult(fundraiserData, fundraiserName));
                });
            }
            const [fundraiser, name] = yield Promise.all([
                sto.fundraisers(rawAssetId, rawU64),
                fetchName(),
            ]);
            return assembleResult(fundraiser, name);
        });
    }
    /**
     * Retrieve all investments made on this Offering
     *
     * @param opts.size - page size
     * @param opts.start - page offset
     *
     * @note supports pagination
     * @note uses the middleware V2
     */
    getInvestments(opts = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { context, id, asset: { id: assetId }, } = this;
            const middlewareAssetId = yield (0, internal_2.getAssetIdForMiddleware)(assetId, context);
            const { size, start } = opts;
            const { data: { investments: { nodes, totalCount }, }, } = yield context.queryMiddleware((0, stos_1.investmentsQuery)(context.isSqIdPadded, {
                stoId: id.toNumber(),
                offeringToken: middlewareAssetId,
            }, size, start));
            const count = new bignumber_js_1.default(totalCount);
            const data = nodes.map(({ investorId: did, offeringTokenAmount, raiseTokenAmount }) => ({
                investor: new internal_1.Identity({ did }, context),
                soldAmount: new bignumber_js_1.default(offeringTokenAmount).shiftedBy(-6),
                investedAmount: new bignumber_js_1.default(raiseTokenAmount).shiftedBy(-6),
            }));
            const next = (0, internal_2.calculateNextKey)(count, data.length, start);
            return {
                data,
                next,
                count,
            };
        });
    }
    /**
     * Determine whether this Offering exists on chain
     */
    exists() {
        return __awaiter(this, void 0, void 0, function* () {
            const { asset, id, context } = this;
            const rawAssetId = (0, conversion_1.assetToMeshAssetId)(asset, context);
            const fundraiser = yield context.polymeshApi.query.sto.fundraisers(rawAssetId, (0, conversion_1.bigNumberToU64)(id, context));
            return fundraiser.isSome;
        });
    }
    /**
     * Return the Offering's ID and Asset ticker
     */
    toHuman() {
        const { asset, id } = this;
        return (0, internal_2.toHumanReadable)({
            assetId: asset,
            id,
        });
    }
}
exports.Offering = Offering;
//# sourceMappingURL=index.js.map